import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Typography,
  Space,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { COUNTRIES, formatCountryDisplay } from '../../constants/countries';

const { Title, Text } = Typography;
const { Option } = Select;

const EntitiesPage = () => {
  const [entities, setEntities] = useState([]);
  const [, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEntities();
    fetchCurrencies();
  }, []);

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/admin/entities');
      setEntities(response.data.data.entities || []);
    } catch (error) {
      message.error('Failed to load entities');
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await apiClient.get('/api/admin/currencies');
      setCurrencies(response.data.currencies || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    }
  };

  const handleAdd = () => {
    setEditingEntity(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (entity) => {
    setEditingEntity(entity);
    form.setFieldsValue(entity);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/api/admin/entities/${id}`);
      message.success('Entity deleted successfully');
      fetchEntities();
    } catch (error) {
      message.error('Failed to delete entity');
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingEntity) {
        await apiClient.put(`/api/admin/entities/${editingEntity.id}`, values);
        message.success('Entity updated successfully');
      } else {
        await apiClient.post('/api/admin/entities', values);
        message.success('Entity created successfully');
      }
      setModalVisible(false);
      fetchEntities();
    } catch (error) {
      message.error(`Failed to ${editingEntity ? 'update' : 'create'} entity`);
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 150,
      render: (country) => <Tag color="green">{formatCountryDisplay(country)}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this entity?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <BankOutlined style={{ marginRight: '8px' }} />
              Transaction Entities Management
            </Title>
            <p style={{ color: '#666', margin: '8px 0 0 0' }}>
              Manage business entities for costing rules
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Entity
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={entities}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} entities`,
          }}
        />
      </Card>

      <Modal
        title={editingEntity ? 'Edit Entity' : 'Add Entity'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Entity Name"
            rules={[{ required: true, message: 'Please enter entity name' }]}
          >
            <Input placeholder="Xendit Indonesia" />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please select a country' }]}
          >
            <Select placeholder="Select country">
              {COUNTRIES.map(country => (
                <Option key={country.code} value={country.code}>
                  {formatCountryDisplay(country.code)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingEntity ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EntitiesPage;
